/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useRef, useState } from "react";
import { graphql } from "gatsby";
import {
  MdFullscreen,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { GatsbyImage } from "gatsby-plugin-image";

import PlayButton from "../components/PlayButton";
import MediaGallery from "../components/MediaGallery";

const Media: React.FC<{
  media:
    | Queries.MediaCarouselImageFragment
    | Queries.MediaCarouselVideoFragment;
  onClick?: () => void;
  className?: string;
}> = ({ media, onClick, className }) => {
  const { title, gatsbyImageData } =
    media.__typename === "ContentfulAsset" ? media : media.thumbnail;

  return (
    <div className="relative">
      <GatsbyImage alt={title} image={gatsbyImageData} className={className} />
      {media.__typename === "ContentfulVimeoVideo" && (
        <button
          className="absolute left-[calc(37.5%)] top-[calc(7/18*100%)] z-[1] w-[25%]"
          aria-label="Play Video"
          onClick={onClick}
        >
          <PlayButton />
        </button>
      )}
    </div>
  );
};

const MediaCarousel: React.FC<{
  media: Array<
    Queries.MediaCarouselImageFragment | Queries.MediaCarouselVideoFragment
  >;
  className?: string;
}> = ({ media, className = "" }) => {
  const clientX = useRef(undefined);
  const scrollLeft = useRef(undefined);
  const frame = useRef<HTMLDivElement>();
  const track = useRef<HTMLDivElement>();
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [activeMedia, setActiveMedia] = useState(0);

  const mouseDownHandler: React.MouseEventHandler = (e) => {
    clientX.current = e.clientX;
    scrollLeft.current = frame.current.scrollLeft;

    document.addEventListener("mouseup", mouseUpHandler);
    document.addEventListener("mousemove", mouseMoveHandler);
  };

  const mouseUpHandler = () => {
    document.removeEventListener("mouseup", mouseUpHandler);
    document.removeEventListener("mousemove", mouseMoveHandler);
    setTimeout(() => setMoving(false), 1);
  };

  const mouseMoveHandler = (e: MouseEvent) => {
    if (!moving) setMoving(true);

    const offset = clientX.current - e.clientX;

    frame.current.scrollTo({
      left: scrollLeft.current + offset,
      behavior: "instant",
    });
  };

  const handleNext = () => {
    const count = media.length;
    const newActiveMedia = (activeMedia + 1) % count;

    frame.current.scrollTo({
      top: (track.current.children[newActiveMedia] as HTMLElement).offsetTop,
      left: (track.current.children[newActiveMedia] as HTMLElement).offsetLeft,
      behavior: "smooth",
    });

    setActiveMedia(newActiveMedia);
  };

  const handlePrevious = () => {
    const count = media.length;
    const newActiveMedia = (activeMedia - 1 + count) % count;

    frame.current.scrollTo({
      top: (track.current.children[newActiveMedia] as HTMLElement).offsetTop,
      left: (track.current.children[newActiveMedia] as HTMLElement).offsetLeft,
      behavior: "smooth",
    });

    setActiveMedia(newActiveMedia);
  };

  return (
    <>
      <MediaGallery
        multiple
        open={open}
        media={media}
        handleNext={handleNext}
        activeMedia={activeMedia}
        handlePrevious={handlePrevious}
        handleClose={() => setOpen(false)}
      />
      <div className={className}>
        <div className="relative mb-2">
          <Media
            media={media[activeMedia]}
            onClick={() => setOpen(true)}
            className="aspect-video rounded-md"
          />
          <div className="absolute right-2 top-2 flex gap-2">
            <div className="rounded bg-black/60 p-3 font-semibold leading-4 text-white">
              {activeMedia + 1} / {media.length}
            </div>
            <div className="rounded bg-black/60 p-1 font-semibold leading-4 text-white transition-colors hover:bg-black/80">
              <button className="block" onClick={() => setOpen(true)}>
                <MdFullscreen className="h-8 w-8 fill-clhbid-orange" />
              </button>
            </div>
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex justify-between">
            <div className="rounded bg-black/60 p-1 font-semibold leading-4 text-white transition-colors hover:bg-black/80">
              <button
                className="block"
                onClick={() => {
                  const newActiveMedia =
                    activeMedia > 0 ? activeMedia - 1 : media.length - 1;

                  frame.current.scrollTo({
                    top: (track.current.children[newActiveMedia] as HTMLElement)
                      .offsetTop,
                    left: (
                      track.current.children[newActiveMedia] as HTMLElement
                    ).offsetLeft,
                    behavior: "smooth",
                  });

                  setActiveMedia(newActiveMedia);
                }}
              >
                <MdKeyboardArrowLeft className="h-8 w-8 fill-clhbid-orange" />
              </button>
            </div>
            <div className="rounded bg-black/60 p-1 font-semibold leading-4 text-white transition-colors hover:bg-black/80">
              <button
                className="block"
                onClick={() => {
                  const newActiveMedia =
                    activeMedia < media.length - 1 ? activeMedia + 1 : 0;

                  frame.current.scrollTo({
                    top: (track.current.children[newActiveMedia] as HTMLElement)
                      .offsetTop,
                    left: (
                      track.current.children[newActiveMedia] as HTMLElement
                    ).offsetLeft,
                    behavior: "smooth",
                  });

                  setActiveMedia(newActiveMedia);
                }}
              >
                <MdKeyboardArrowRight className="h-8 w-8 fill-clhbid-orange" />
              </button>
            </div>
          </div>
        </div>
        <div className="w-full overflow-hidden">
          <div
            ref={frame}
            onMouseDown={mouseDownHandler}
            className={[
              "scrollbar-hidden -mx-4 w-[calc(100%+32px)] overflow-x-auto px-4 lg:mx-0 lg:w-full lg:px-0",
              moving
                ? ""
                : "snap-x snap-mandatory scroll-pl-4 scroll-smooth lg:scroll-pl-0",
            ].join(" ")}
          >
            <div
              ref={track}
              className="relative flex w-fit justify-start gap-2 pb-4"
            >
              {media.map((media, i) => (
                <button
                  key={media.id}
                  onClick={() => {
                    if (!moving) {
                      setActiveMedia(i);
                    }
                  }}
                  className="relative block snap-start overflow-hidden rounded-md"
                >
                  <Media
                    media={media}
                    className="pointer-events-none aspect-video w-32 lg:w-[176px]"
                  />
                  {i === activeMedia && (
                    <div className="absolute inset-0 z-[1] rounded-md border-2 border-clhbid-orange" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const MediaCarouselFragment = graphql`
  fragment MediaCarouselImage on ContentfulAsset {
    __typename
    id
    title
    description
    gatsbyImageData
  }

  fragment MediaCarouselVideo on ContentfulVimeoVideo {
    __typename
    id
    title
    description
    url
    thumbnail {
      id
      title
      gatsbyImageData
    }
  }
`;

export default MediaCarousel;
